import React, { useState } from 'react';
import { usePoints } from '../../context/PointsContext';
import PointsHistoryModal from './PointsHistoryModal';
import styles from './PointsBalanceCard.module.css';

const PointsBalanceCard: React.FC = () => {
  const { balance, isEnabled, planType, isLoading, error } = usePoints();
  const [showHistoryModal, setShowHistoryModal] = useState(false);

  if (!isEnabled) return null;
  if (isLoading) return <div className={styles.loading}>Loading points...</div>;
  if (error) return <div className={styles.error}>Error: {error}</div>;
  if (!balance) return null;

  const getBalanceColor = () => {
    if (balance.isUnlimited) return '#10b981'; // Green for unlimited
    const remaining = balance.pointsRemaining;
    const total = balance.monthlyPoints;
    const percentage = (remaining / total) * 100;
    
    if (percentage > 50) return '#10b981'; // Green
    if (percentage > 25) return '#f59e0b'; // Orange
    return '#ef4444'; // Red
  };

  const formatNumber = (num: number) => {
    return new Intl.NumberFormat().format(num);
  };

  return (
    <>
      <div className={styles.card}>
        <div className={styles.header}>
          <h3 className={styles.title}>
            {balance.isUnlimited ? 'Daily Usage' : 'Points Balance'}
          </h3>
          <div className={styles.headerRight}>
            <div className={styles.planBadge}>
              {planType === 'unlimited' ? 'Unlimited' : 'Points Plan'}
            </div>
          </div>
        </div>

        {balance.isUnlimited ? (
          <div className={styles.unlimitedStats}>
            <div className={styles.stat}>
              <span className={styles.statLabel}>Used Today</span>
              <span className={styles.statValue} style={{ color: getBalanceColor() }}>
                {formatNumber(balance.dailyUsed || 0)}
              </span>
            </div>
            <div className={styles.stat}>
              <span className={styles.statLabel}>Daily Limit</span>
              <span className={styles.statValue}>
                {formatNumber(balance.dailyLimit || 0)}
              </span>
            </div>
            <div className={styles.progressBar}>
              <div 
                className={styles.progress} 
                style={{ 
                  width: `${Math.min(((balance.dailyUsed || 0) / (balance.dailyLimit || 1)) * 100, 100)}%`,
                  backgroundColor: getBalanceColor()
                }} 
              />
            </div>
          </div>
        ) : (
          <div className={styles.pointsStats}>
            <div className={styles.mainBalance}>
              <span className={styles.remainingPoints} style={{ color: getBalanceColor() }}>
                {formatNumber(balance.pointsRemaining)}
              </span>
              <span className={styles.totalPoints}>
                / {formatNumber(balance.monthlyPoints)} points
              </span>
            </div>
            <div className={styles.usedInfo}>
              <span className={styles.usedText}>
                {formatNumber(balance.pointsUsed)} used this month
              </span>
            </div>
            <div className={styles.progressBar}>
              <div 
                className={styles.progress} 
                style={{ 
                  width: `${Math.min((balance.pointsUsed / balance.monthlyPoints) * 100, 100)}%`,
                  backgroundColor: getBalanceColor()
                }} 
              />
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className={styles.actionSection}>
          <button 
            className={styles.upgradeButton}
            onClick={() => window.location.href = '/pricing'}
          >
            <span className={styles.buttonIcon}>⚡</span>
            <span>Upgrade Plan</span>
          </button>
          
          <button 
            className={styles.buyPointsButton}
            onClick={() => {
              // Navigate to pricing page with hash
              window.location.href = '/pricing#points-section';
              // After navigation, scroll to the section
              setTimeout(() => {
                const section = document.getElementById('points-section');
                if (section) {
                  section.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
              }, 100);
            }}
          >
            <span className={styles.buttonIcon}>💎</span>
            <span>Buy Extra Points</span>
          </button>
          
          <button 
            className={styles.historyButton}
            onClick={() => setShowHistoryModal(true)}
          >
            <span className={styles.buttonIcon}>📊</span>
            <span>Transaction Log</span>
          </button>
        </div>
      </div>

      {/* History Modal */}
      <PointsHistoryModal
        isOpen={showHistoryModal}
        onClose={() => setShowHistoryModal(false)}
      />
    </>
  );
};

export default PointsBalanceCard;
